<!-- ImpactFlow.vue -->
<template>
  <section class="w-full" style="background-color: #E4F4FC;">
    <!--
      Scalable wrapper: uses a fixed 1281×499 internal coordinate space
      that scales proportionally via aspect-ratio + width:100%.
      All child positions are in the 1281×499 space using absolute %.

      DESKTOP layout (md and up). The mobile layout below uses a separate
      portrait coordinate space that stacks the circles into a zig-zag.
    -->
    <div
      class="relative w-full mx-auto overflow-hidden hidden md:block"
      style="max-width: 1281px; aspect-ratio: 1281 / 499; background-color: #E4F4FC;"
    >

      <!-- ════════════════════════════════════════════════
           DASHED CONNECTOR LINE
           Figma: left=273, top=194, width=778, height=97.5
           Path: M0.97 2.64 L219.47 94.14 L488.47 2.64 L778.97 100.14
           Stroke: #D9D9D9, width=5px, dashed
      ════════════════════════════════════════════════ -->
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 778.97 100.14"
        style="
          position: absolute;
          left: calc(273 / 1281 * 100%);
          top: calc(194 / 499 * 100%);
          width: calc(778 / 1281 * 100%);
          height: calc(97.5 / 499 * 100%);
          overflow: visible;
        "
      >
        <path
          d="M0.965698 2.63867 L219.466 94.1387 L488.466 2.63867 L778.966 100.139"
          fill="none"
          stroke="#D9D9D9"
          stroke-width="5"
          stroke-dasharray="14 9"
          stroke-linecap="round"
          stroke-linejoin="round"
          vector-effect="non-scaling-stroke"
        />
      </svg>

      <!-- ════════════════════════════════════════════════
           CIRCLE 1: Teachers empowered
           Figma: left=179, top=111, size=138×138
      ════════════════════════════════════════════════ -->
      <div
        class="absolute flex items-center justify-center rounded-full"
        style="
          left: calc(179 / 1281 * 100%);
          top: calc(111 / 499 * 100%);
          width: calc(138 / 1281 * 100%);
          aspect-ratio: 1 / 1;
        "
      >
        <img :src="image1" :alt="label1" class="w-[138px] h-[138px] object-contain" />
      </div>
      <!-- Label 1 -->
      <div
        class="absolute"
        style="
          left: calc((179 + 69 - 84) / 1281 * 100%);
          top: calc((111 + 138 + 16) / 499 * 100%);
          width: calc(168 / 1281 * 100%);
          font-family: 'Outfit', sans-serif;
          font-size: clamp(10px, calc(20 / 1281 * 100vw), 20px);
          font-weight: 500;
          color: #000000;
          text-align: center;
          line-height: 1.35;
          white-space: nowrap;
        "
      >
        {{ label1 }}
      </div>

      <!-- ════════════════════════════════════════════════
           CIRCLE 2: Students engaged
           Figma: left=421, top=211, size=138×138
      ════════════════════════════════════════════════ -->
      <div
        class="absolute flex items-center justify-center rounded-full"
        style="
          left: calc(421 / 1281 * 100%);
          top: calc(211 / 499 * 100%);
          width: calc(138 / 1281 * 100%);
          aspect-ratio: 1 / 1;
        "
      >
        <img :src="image2" :alt="label2" class="w-[138px] h-[138px] object-contain" />
      </div>
      <!-- Label 2 -->
      <div
        class="absolute"
        style="
          left: calc((421 + 69 - 84) / 1281 * 100%);
          top: calc((211 + 138 + 16) / 499 * 100%);
          width: calc(168 / 1281 * 100%);
          font-family: 'Outfit', sans-serif;
          font-size: clamp(10px, calc(20 / 1281 * 100vw), 20px);
          font-weight: 500;
          color: #000000;
          text-align: center;
          line-height: 1.35;
          white-space: nowrap;
        "
      >
        {{ label2 }}
      </div>

      <!-- ════════════════════════════════════════════════
           CIRCLE 3: Communities inspired
           Figma: left=687, top=117, size=137×138
      ════════════════════════════════════════════════ -->
      <div
        class="absolute flex items-center justify-center rounded-full"
        style="
          left: calc(687 / 1281 * 100%);
          top: calc(117 / 499 * 100%);
          width: calc(137 / 1281 * 100%);
          aspect-ratio: 1 / 1;
        "
      >
        <img :src="image3" :alt="label3" class="w-[138px] h-[138px] object-contain" />
      </div>
      <!-- Label 3 -->
      <div
        class="absolute"
        style="
          left: calc((687 + 68.5 - 84) / 1281 * 100%);
          top: calc((117 + 138 + 16) / 499 * 100%);
          width: calc(168 / 1281 * 100%);
          font-family: 'Outfit', sans-serif;
          font-size: clamp(10px, calc(20 / 1281 * 100vw), 20px);
          font-weight: 500;
          color: #000000;
          text-align: center;
          line-height: 1.35;
          white-space: nowrap;
        "
      >
        {{ label3 }}
      </div>

      <!-- ════════════════════════════════════════════════
           CIRCLE 4: Climate action amplified
           Figma: left=976, top=210, size=138×138
      ════════════════════════════════════════════════ -->
      <div
        class="absolute flex items-center justify-center rounded-full"
        style="
          left: calc(976 / 1281 * 100%);
          top: calc(210 / 499 * 100%);
          width: calc(138 / 1281 * 100%);
          aspect-ratio: 1 / 1;
        "
      >
        <img :src="image4" :alt="label4" class="w-[138px] h-[138px] object-contain" />
      </div>
      <!-- Label 4 -->
      <div
        class="absolute"
        style="
          left: calc((976 + 69 - 84) / 1281 * 100%);
          top: calc((210 + 138 + 16) / 499 * 100%);
          width: calc(168 / 1281 * 100%);
          font-family: 'Outfit', sans-serif;
          font-size: clamp(10px, calc(20 / 1281 * 100vw), 20px);
          font-weight: 500;
          color: #000000;
          text-align: center;
          line-height: 1.35;
          white-space: nowrap;
        "
      >
        {{ label4 }}
      </div>

    </div>

    <!-- ════════════════════════════════════════════════════════════
         MOBILE layout (below md): vertical zig-zag.
         Uses a fixed 460×540 portrait coordinate space that scales
         proportionally via aspect-ratio + width:100%.
         Columns alternate: C1 left, C2 right, C3 left, C4 right,
         each stepping down, joined by dashed diagonal connectors.
    ════════════════════════════════════════════════════════════ -->
    <div
      class="relative w-full mx-auto overflow-hidden block md:hidden"
      style="max-width: 460px; aspect-ratio: 460 / 540; background-color: #E4F4FC;"
    >
      <!-- DASHED CONNECTORS (edge-to-edge between circle centers, r=55) -->
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 460 540"
        style="position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible;"
      >
        <!-- C1 → C2 -->
        <path
          d="M171.7 113.8 L288.3 156.2"
          fill="none" stroke="#D9D9D9" stroke-width="4"
          stroke-dasharray="12 8" stroke-linecap="round"
          vector-effect="non-scaling-stroke"
        />
        <!-- C2 → C3 -->
        <path
          d="M293.6 204.5 L166.4 285.5"
          fill="none" stroke="#D9D9D9" stroke-width="4"
          stroke-dasharray="12 8" stroke-linecap="round"
          vector-effect="non-scaling-stroke"
        />
        <!-- C3 → C4 -->
        <path
          d="M168.7 340.6 L271.3 394.4"
          fill="none" stroke="#D9D9D9" stroke-width="4"
          stroke-dasharray="12 8" stroke-linecap="round"
          vector-effect="non-scaling-stroke"
        />
      </svg>

      <!-- CIRCLE 1: Teachers empowered (top-left) -->
      <div
        class="absolute flex items-center justify-center rounded-full"
        style="left: calc(65 / 460 * 100%); top: calc(40 / 540 * 100%); width: calc(110 / 460 * 100%); aspect-ratio: 1 / 1;"
      >
        <img :src="image1" :alt="label1" class="w-full h-full object-contain" />
      </div>
      <div
        class="absolute"
        style="
          left: calc(50 / 460 * 100%);
          top: calc(158 / 540 * 100%);
          width: calc(140 / 460 * 100%);
          font-family: 'Outfit', sans-serif;
          font-size: clamp(14px, calc(18 / 460 * 100vw), 20px);
          font-weight: 500; color: #000000; text-align: center; line-height: 1.3;
        "
      >
        {{ label1 }}
      </div>

      <!-- CIRCLE 2: Students engaged (right) -->
      <div
        class="absolute flex items-center justify-center rounded-full"
        style="left: calc(285 / 460 * 100%); top: calc(120 / 540 * 100%); width: calc(110 / 460 * 100%); aspect-ratio: 1 / 1;"
      >
        <img :src="image2" :alt="label2" class="w-full h-full object-contain" />
      </div>
      <div
        class="absolute"
        style="
          left: calc(270 / 460 * 100%);
          top: calc(238 / 540 * 100%);
          width: calc(140 / 460 * 100%);
          font-family: 'Outfit', sans-serif;
          font-size: clamp(14px, calc(18 / 460 * 100vw), 20px);
          font-weight: 500; color: #000000; text-align: center; line-height: 1.3;
        "
      >
        {{ label2 }}
      </div>

      <!-- CIRCLE 3: Communities inspired (left) -->
      <div
        class="absolute flex items-center justify-center rounded-full"
        style="left: calc(65 / 460 * 100%); top: calc(260 / 540 * 100%); width: calc(110 / 460 * 100%); aspect-ratio: 1 / 1;"
      >
        <img :src="image3" :alt="label3" class="w-full h-full object-contain" />
      </div>
      <div
        class="absolute"
        style="
          left: calc(50 / 460 * 100%);
          top: calc(378 / 540 * 100%);
          width: calc(140 / 460 * 100%);
          font-family: 'Outfit', sans-serif;
          font-size: clamp(14px, calc(18 / 460 * 100vw), 20px);
          font-weight: 500; color: #000000; text-align: center; line-height: 1.3;
        "
      >
        {{ label3 }}
      </div>

      <!-- CIRCLE 4: Climate action amplified (bottom-right) -->
      <div
        class="absolute flex items-center justify-center rounded-full"
        style="left: calc(265 / 460 * 100%); top: calc(365 / 540 * 100%); width: calc(110 / 460 * 100%); aspect-ratio: 1 / 1;"
      >
        <img :src="image4" :alt="label4" class="w-full h-full object-contain" />
      </div>
      <div
        class="absolute"
        style="
          left: calc(250 / 460 * 100%);
          top: calc(483 / 540 * 100%);
          width: calc(140 / 460 * 100%);
          font-family: 'Outfit', sans-serif;
          font-size: clamp(14px, calc(18 / 460 * 100vw), 20px);
          font-weight: 500; color: #000000; text-align: center; line-height: 1.3;
        "
      >
        {{ label4 }}
      </div>

    </div>
  </section>
</template>

<script>
export default {
    props: {
        label1: { type: String, default: 'Teachers empowered' },
        image1: { type: String, default: '/img/about-us/impact/teachers-empowered.png' },
        label2: { type: String, default: 'Students engaged' },
        image2: { type: String, default: '/img/about-us/impact/students-engaged.png' },
        label3: { type: String, default: 'Communities inspired' },
        image3: { type: String, default: '/img/about-us/impact/communities-inspired.png' },
        label4: { type: String, default: 'Climate action amplified' },
        image4: { type: String, default: '/img/about-us/impact/climate-action-amplified.png' },
    }
}
</script>